import React, { useState, useEffect, useMemo, useRef } from "react";
import styled from "styled-components";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import { Dimmer, Loader, Header, Modal, Dropdown } from "semantic-ui-react";
import { createPortal } from "react-dom";
import ContactBreadcrumb from "./ContactBreadcrumb";
import {
  SG_EDIT_EMPLOYEE_CATEGORY,
  SG_ADD_EMPLOYEE_CATEGORY,
  SG_GET_EMPLOYEE_ORG_CATEGORIES,
  SG_UPDATE_EMPLOYEE_AUDIT,
  SG_GET_SURVEY_STRUCTURES,
  LOAD_EMPLOYEES,
  PULL_EMPLOYEE,
  SG_GET_EMPLOYEE_PERMISSIONS,
  UPDATE_EMPLOYEE,
  SG_ADD_EMPLOYEE_LOG,
} from "constants/actions";

const PopoverPortal = ({ children }) => {
  if (typeof document === "undefined") return null;
  return createPortal(children, document.body);
};

const EmployeeEditV2 = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { addToast } = useToasts();
  const { id } = useParams();



  const [loading, setLoading] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [employeeCategoryId, setEmployeeCategoryId] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState({});
  const [primaryTeamSelections, setPrimaryTeamSelections] = useState([]);
  const [catDispatch, setCatDispatch] = useState(false);
  const [showTagEditor, setShowTagEditor] = useState(false);
  const [editingCategory, setEditingCategory] = useState('');
  const [editingValue, setEditingValue] = useState('');
  const [hasUnsavedTagChanges, setHasUnsavedTagChanges] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archiveReason, setArchiveReason] = useState("");
  const [nonSportOrg, setNonSportOrg] = useState(false);
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [optionsAnchor, setOptionsAnchor] = useState(null);
  const optionsButtonRef = useRef(null);
  const optionsMenuRef = useRef(null);
  const userEmail = selectedEmployee?.email || "";

  const {
    get_auth,
    get_employees,
    get_employee_category,
    get_survey_structure,
    core_data,
    categoriesFetched,
    categoryUpdated,
    get_organizations,
    get_org_id,
  } = useSelector(
    (state) => ({
      get_auth: state.auth,
      get_employees: state.employees,
      get_employee_category: state.employee_category.employee_org_category,
      get_survey_structure: state.surveystructure.survey_structure,
      core_data: state.audit?.core_data,
      categoriesFetched: state.employee_category.fetched,
      categoryUpdated: state.employee_category.updated,
      get_organizations: state.organizations,
      get_org_id: state.auth.organization_id,
    }),
    shallowEqual
  );



  // Load employee data
  useEffect(() => {
    if (!get_employees?.employees) {
      dispatch({ type: LOAD_EMPLOYEES });
    }
  }, [dispatch, get_employees]);

  useEffect(() => {
    if (id) {
      dispatch({
        type: PULL_EMPLOYEE,
        payload: {id}
      })
    }
  }, [id]);

  // Load survey structure and categories
  useEffect(() => {
    if (selectedEmployee && (!get_survey_structure || get_survey_structure.length === 0)) {
      dispatch({
        type: SG_GET_SURVEY_STRUCTURES,
        payload: `organization=${selectedEmployee?.organization}`,
      });
    }
  }, [dispatch, selectedEmployee, get_survey_structure]);

  // Load employee categories
  useEffect(() => {
    if (get_employees && id) {
      if (id && !catDispatch && get_auth?.organization_id) {
        setCatDispatch(true);
        
        const employeeCategory = get_employee_category?.find((c) => {
          return c?.employee_id === Number(id);
        });

        if (!employeeCategory) {
          dispatch({
            type: SG_GET_EMPLOYEE_ORG_CATEGORIES,
            payload: `organization=${get_auth?.organization_id}`,
          });
        }
      }
    }
  }, [dispatch, get_employees, id, get_auth, catDispatch, get_employee_category]);

  // Set selected employee
  useEffect(() => {
    if (id && get_employees?.employees) {
      const employee = get_employees.employees.find(e => e.id === Number(id));
      if (employee) {
        setSelectedEmployee(employee);
        setFirstName(employee.first_name || "");
        setLastName(employee.last_name || "");
      }

    }
  }, [id, get_employees]);

  // Process employee categories
  useEffect(() => {
    if (get_employee_category?.length > 0 && id) {
      const employeeCategory = get_employee_category.find((c) => {
        return c?.employee_id === Number(id);
      });

      if (employeeCategory) {
        setEmployeeCategoryId(employeeCategory.id);
        
        // Separate primary and other categories
        const primaryCats = employeeCategory?.category?.filter(c => c?.priority === "primary") || [];
        const otherCats = employeeCategory?.category?.filter(c => c?.priority !== "primary") || [];
        
        setPrimaryTeamSelections(primaryCats?.map(c => c?.value_id));
        
        // Process other categories into selectedCategories object
        const categorySelections = {};
        otherCats?.forEach(cat => {
          categorySelections[cat?.name_id] = cat?.value_id;
        });
        setSelectedCategories(categorySelections);
        

      } else {
        // Reset if no categories found
        setEmployeeCategoryId(null);
        setPrimaryTeamSelections([]);
        setSelectedCategories({});

      }
    }
  }, [get_employee_category, id]);

  // Determine when loading is complete
  useEffect(() => {
    if (
      selectedEmployee &&
      get_survey_structure?.[0] &&
      (employeeCategoryId !== null || (catDispatch && categoriesFetched))
    ) {
      setLoading(false);
    }
  }, [selectedEmployee, get_survey_structure, employeeCategoryId, catDispatch, categoriesFetched, loading]);

  // Listen for category updates and refetch data
  useEffect(() => {
    if (categoryUpdated) {
      dispatch({
        type: SG_GET_EMPLOYEE_ORG_CATEGORIES,
        payload: `organization=${get_auth?.organization_id}`,
      });
    }
  }, [categoryUpdated, dispatch, get_auth]);

  useEffect(() => {
    const orgCategory = get_organizations?.[get_org_id]?.org_category;
    if (!orgCategory) return;

    const sportFlag = orgCategory?.sport;

    if (sportFlag === "yes") {
      setNonSportOrg(false);
      return;
    }

    if (sportFlag === "no" || sportFlag === false) {
      setNonSportOrg(true);
      return;
    }

    // Default to allowing archive for non-sport configurations.
    setNonSportOrg(true);
  }, [get_organizations, get_org_id]);

  useEffect(() => {
    if (!showOptionsMenu) {
      return undefined;
    }

    const handleClickAway = (event) => {
      const target = event.target;
      if (
        (optionsButtonRef.current && optionsButtonRef.current.contains(target)) ||
        (optionsMenuRef.current && optionsMenuRef.current.contains(target))
      ) {
        return;
      }
      setShowOptionsMenu(false);
      setOptionsAnchor(null);
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape" || event.key === "Esc") {
        setShowOptionsMenu(false);
        setOptionsAnchor(null);
      }
    };

    document.addEventListener("mousedown", handleClickAway);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickAway);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showOptionsMenu]);

  // Get available categories (filter out private/hidden)
  const visibleCategories = useMemo(() => {
    const categories = core_data?.categories?.[0]?.categories || get_survey_structure?.[0]?.categories;
    if (!categories) return [];
    
    return categories.filter(cat => {
      if (cat.visibility === "private") return false;
      return true;
    });
  }, [core_data, get_survey_structure]);

  // Get primary category options
  const primaryCategory = useMemo(() => {
    return visibleCategories.find(cat => cat.priority === "primary");
  }, [visibleCategories]);

  // Get other categories (non-primary)
  const otherCategories = useMemo(() => {
    return visibleCategories.filter(cat => cat.priority !== "primary");
  }, [visibleCategories]);



  const handlePrimaryToggle = (optionId) => {
    setPrimaryTeamSelections(prev => {
      if (prev.includes(optionId)) {
        const next = prev.filter(id => id !== optionId);
        setHasUnsavedTagChanges(true);
        return next;
      } else {
        const next = [...prev, optionId];
        setHasUnsavedTagChanges(true);
        return next;
      }
    });
  };

  const handleCategorySelect = (categoryId, optionId) => {
    setSelectedCategories(prev => ({
      ...prev,
      [categoryId]: optionId
    }));
    setHasUnsavedTagChanges(true);
  };

  const handleCategoryClear = (categoryId) => {
    setSelectedCategories(prev => {
      const newSelections = { ...prev };
      delete newSelections[categoryId];
      setHasUnsavedTagChanges(true);
      return newSelections;
    });
  };

  const getOptionsForCategory = (categoryKey) => {
    if (categoryKey.startsWith('primary-')) {
      return primaryCategory?.options || [];
    }
    
    const category = otherCategories.find(cat => cat.id === categoryKey);
    return category?.options || [];
  };

  // Format category options for Semantic UI Dropdown
  const getCategoryDropdownOptions = () => {
    const options = [];
    
    if (primaryCategory) {
      options.push({
        key: `primary-${primaryCategory.id}`,
        text: `${primaryCategory.name} (can select multiple)`,
        value: `primary-${primaryCategory.id}`
      });
    }
    
    otherCategories.forEach(cat => {
      options.push({
        key: cat.id,
        text: cat.name,
        value: cat.id
      });
    });
    
    return options;
  };

  // Format value options for Semantic UI Dropdown
  const getValueDropdownOptions = () => {
    if (!editingCategory) return [];
    
    const categoryOptions = getOptionsForCategory(editingCategory);
    return categoryOptions.map(option => ({
      key: option.id,
      text: option.name,
      value: option.id
    }));
  };

  const handleAddTag = () => {
    if (!editingCategory || !editingValue) return;

    if (editingCategory.startsWith('primary-')) {
      // Handle primary category (can have multiple). Preserve option id type.
      const chosen = (primaryCategory?.options || []).find(
        (opt) => String(opt.id) === String(editingValue)
      );
      if (!chosen) return;

      setPrimaryTeamSelections((prev) => {
        const exists = prev.some((id) => String(id) === String(chosen.id));
        if (exists) return prev;
        setHasUnsavedTagChanges(true);
        return [...prev, chosen.id];
      });
    } else {
      // Handle other categories (single selection)
      setSelectedCategories((prev) => ({
        ...prev,
        [editingCategory]: editingValue, // Keep as-is; option lookup handles type
      }));
      setHasUnsavedTagChanges(true);
    }

    // Reset editor
    setEditingCategory('');
    setEditingValue('');
    setShowTagEditor(false);
  };

  const closeOptionsMenu = () => {
    setShowOptionsMenu(false);
    setOptionsAnchor(null);
  };

  const handleToggleOptionsMenu = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    if (showOptionsMenu) {
      closeOptionsMenu();
      return;
    }
    setOptionsAnchor(rect);
    setShowOptionsMenu(true);
  };

  const handleCopyEmail = async () => {
    if (!userEmail) {
      addToast("No email available to copy.", {
        appearance: "warning",
        autoDismiss: true,
      });
      return;
    }

    try {
      if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(userEmail);
      } else if (typeof document !== "undefined") {
        const fallbackInput = document.createElement("input");
        fallbackInput.value = userEmail;
        document.body.appendChild(fallbackInput);
        fallbackInput.select();
        document.execCommand("copy");
        document.body.removeChild(fallbackInput);
      } else {
        throw new Error("Clipboard unavailable");
      }

      addToast("Email copied to clipboard.", {
        appearance: "success",
        autoDismiss: true,
      });
    } catch (err) {
      addToast("Unable to copy email. Please copy it manually.", {
        appearance: "error",
        autoDismiss: true,
      });
    }
  };

  const handleMenuCopyEmail = async () => {
    await handleCopyEmail();
    closeOptionsMenu();
  };

  const handleMenuDeleteContact = () => {
    closeOptionsMenu();
    setArchiveReason("");
    setShowArchiveModal(true);
  };

  const handleSave = () => {
    // Build primary category selections
    const primaryCatSelections = primaryTeamSelections.map(valueId => {
      const option = primaryCategory?.options.find(opt => String(opt.id) === String(valueId));
      if (!option) return null;
      
      return {
        name: primaryCategory.name,
        name_id: primaryCategory.id,
        value: option.name,
        value_id: option.id,
        priority: "primary",
      };
    }).filter(Boolean);

    // Build other category selections
    const categorySelections = Object.entries(selectedCategories).map(([categoryId, optionId]) => {
      const category = otherCategories.find(cat => String(cat.id) === String(categoryId));
      const option = category?.options.find(opt => String(opt.id) === String(optionId));
      
      if (!category || !option) return null;
      
      return {
        name: category.name,
        name_id: category.id,
        value: option.name,
        value_id: option.id,
        priority: category.priority,
      };
    }).filter(Boolean);

    const allCategorySelections = [...primaryCatSelections, ...categorySelections];

    // Save categories
    if (employeeCategoryId) {
      dispatch({
        type: SG_EDIT_EMPLOYEE_CATEGORY,
        payload: {
          category: allCategorySelections,
          id: employeeCategoryId,
        },
      });
    } else {
      dispatch({
        type: SG_ADD_EMPLOYEE_CATEGORY,
        payload: {
          category: allCategorySelections,
          employee: selectedEmployee?.id,
        },
      });
    }

    // Save name changes
    dispatch({
      type: SG_UPDATE_EMPLOYEE_AUDIT,
      payload: {
        first_name: firstName,
        last_name: lastName,
        id: selectedEmployee.id,
      },
    });

    addToast("Contact updated successfully!", { 
      appearance: "success", 
      autoDismiss: true 
    });
    setHasUnsavedTagChanges(false);
    setSelectedEmployee((prev) =>
      prev
        ? {
            ...prev,
            first_name: firstName,
            last_name: lastName,
          }
        : prev
    );
    
    // Refetch employee categories to get updated data
    setTimeout(() => {
      dispatch({
        type: SG_GET_EMPLOYEE_ORG_CATEGORIES,
        payload: `organization=${get_auth?.organization_id}`,
      });
    }, 500);
    
    // Stay on page to see changes, or navigate back
    // navigate("/leader/audit/contact-list-v2/");
  };

  const handleCancel = () => {
    navigate("/leader/audit/contact-list-v2/");
  };

  const handleArchive = () => {
    if (!selectedEmployee) return;

    const canArchive =
      get_organizations?.[get_org_id]?.org_category ? nonSportOrg : true;

    const trimmedReason = archiveReason.trim();

    if (!canArchive) {
      addToast("Archiving is not enabled for this organization.", {
        appearance: "error",
        autoDismiss: true,
      });
      return;
    }

    dispatch({
      type: UPDATE_EMPLOYEE,
      payload: {
        id: selectedEmployee.id,
        status: 99,
      },
    });

    dispatch({
      type: SG_ADD_EMPLOYEE_LOG,
      payload: {
        employee: selectedEmployee.id,
        type_of_log: 2,
        description: {
          Archived: trimmedReason || "No reason provided",
        },
      },
    });

    addToast("Contact archived successfully.", {
      appearance: "success",
      autoDismiss: true,
    });

    setShowArchiveModal(false);
    setArchiveReason("");
    navigate("/leader/audit/contact-list-v2/");
  };

  const handleReactivate = () => {
    if (!selectedEmployee) return;

    const canReactivate =
      get_organizations?.[get_org_id]?.org_category ? nonSportOrg : true;

    if (!canReactivate) {
      addToast("Re-activating is not enabled for this organization.", {
        appearance: "error",
        autoDismiss: true,
      });
      return;
    }

    dispatch({
      type: UPDATE_EMPLOYEE,
      payload: {
        id: selectedEmployee.id,
        status: 1,
      },
    });

    dispatch({
      type: SG_ADD_EMPLOYEE_LOG,
      payload: {
        employee: selectedEmployee.id,
        type_of_log: 2,
        description: {
          Archived: "Re-activate employee",
        },
      },
    });

    addToast("Contact re-activated successfully.", {
      appearance: "success",
      autoDismiss: true,
    });

    setSelectedEmployee((prev) =>
      prev
        ? {
            ...prev,
            status: 1,
          }
        : prev
    );
  };

  useEffect(() => {
    if (!id) {
      navigate("/leader/audit/contact-list-v2/");
    }
  }, [id, navigate]);

  const breadcrumbCrumbs = useMemo(() => {
    if (!selectedEmployee) {
      return [{ label: "Edit Contact" }];
    }

    const name =
      `${selectedEmployee.first_name || ""} ${selectedEmployee.last_name || ""}`.trim();
    const label = name || selectedEmployee.email || "Edit Contact";

    return [{ label }];
  }, [selectedEmployee]);

  const initialFirstName = selectedEmployee?.first_name || "";
  const initialLastName = selectedEmployee?.last_name || "";
  const formHasChanges = selectedEmployee
    ? firstName !== initialFirstName || lastName !== initialLastName || hasUnsavedTagChanges
    : false;

  if (loading) {
    return (
      <LoadingContainer>
        <Dimmer active>
          <Loader />
          <Header as="h2" icon inverted>
            Loading contact data...
          </Header>
        </Dimmer>
      </LoadingContainer>
    );
  }

  if (!selectedEmployee) {
    return (
      <Container>
        <Header>Contact not found</Header>
      </Container>
    );
  }

  return (
    <Container>
      <Modal
        open={showArchiveModal}
        onClose={() => {
          setShowArchiveModal(false);
          setArchiveReason("");
        }}
        size="tiny"
        closeOnDimmerClick={false}
      >
        <Modal.Header>Delete Contact</Modal.Header>
        <Modal.Content>
          <ModalDescription>
            Deleting the contact will move them from this list, add them into the inactive list and revoke
            their access. You can optionally share a reason to record in the
            audit log.
          </ModalDescription>
          <ArchiveReasonInput
            value={archiveReason}
            onChange={(e) => setArchiveReason(e.target.value)}
            placeholder="Delete reason (optional)"
            rows={3}
          />
        </Modal.Content>
        <Modal.Actions>
          <ModalButton
            $secondary
            onClick={() => {
              setArchiveReason("");
              setShowArchiveModal(false);
            }}
          >
            Cancel
          </ModalButton>
          <ModalButton onClick={handleArchive}>Delete</ModalButton>
        </Modal.Actions>
      </Modal>
      <ContactBreadcrumb crumbs={breadcrumbCrumbs} />
      <HeaderSection>
        <Title>Edit Contact</Title>
        <ActionButtons>
          {hasUnsavedTagChanges && (
            <UnsavedPill title="You have unsaved tag changes">
              Unsaved changes
            </UnsavedPill>
          )}
          <PrimaryActions>
            <SaveButton onClick={handleSave} disabled={!formHasChanges}>
              Save Changes
            </SaveButton>
            <OptionsMenuWrapper>
              <MenuTriggerButton
                ref={optionsButtonRef}
                type="button"
                onClick={handleToggleOptionsMenu}
                aria-haspopup="menu"
                aria-expanded={showOptionsMenu}
                aria-label="More actions"
              >
                <MoreVertIcon />
              </MenuTriggerButton>
              {showOptionsMenu && optionsAnchor && (
                <PopoverPortal>
                  <FloatingMenu
                    ref={optionsMenuRef}
                    style={{
                      position: "fixed",
                      top: optionsAnchor.bottom + 4,
                      left: optionsAnchor.right,
                      transform: "translateX(-100%)",
                    }}
                  >
                    <OptionsMenuItem
                      type="button"
                      onClick={handleMenuCopyEmail}
                      disabled={!userEmail}
                    >
                      Copy Email
                    </OptionsMenuItem>
                    {selectedEmployee?.status === 1 && (
                      <>
                        <OptionsMenuDivider />
                        <OptionsMenuItem
                          type="button"
                          danger
                          onClick={handleMenuDeleteContact}
                        >
                          Delete Contact
                        </OptionsMenuItem>
                      </>
                    )}
                  </FloatingMenu>
                </PopoverPortal>
              )}
            </OptionsMenuWrapper>
          </PrimaryActions>
        </ActionButtons>
      </HeaderSection>

      <ContentSection>
        {/* Basic Information */}
        <Section>
          <SectionTitle>Basic Information</SectionTitle>
          <FieldGrid>
            <FieldGroup>
              <FieldLabel>First Name</FieldLabel>
              <FieldInput
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Enter first name"
              />
            </FieldGroup>
            <FieldGroup>
              <FieldLabel>Last Name</FieldLabel>
              <FieldInput
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Enter last name"
              />
            </FieldGroup>
            <FieldGroupFullWidth>
              <FieldLabel>Email</FieldLabel>
              <EmailFieldRow>
                <ReadOnlyField
                  type="text"
                  value={userEmail}
                  readOnly
                  onFocus={(e) => e.target.select()}
                  style={{
                    width: 300
                  }}
                />
              </EmailFieldRow>
            </FieldGroupFullWidth>
          </FieldGrid>
        </Section>

        {/* Tags Section */}
        <Section>
          <TagsSectionHeader>
            <TagsIcon>🏷️</TagsIcon>
            <TagsSectionTitle>Tags</TagsSectionTitle>
            <EditTagsButton onClick={() => setShowTagEditor(!showTagEditor)}>
              Edit Tags
            </EditTagsButton>
          </TagsSectionHeader>

          {/* Current Tags Display */}
          <CurrentTagsContainer>
            {/* Primary Tags */}
            {primaryCategory && primaryTeamSelections.map(valueId => {
              const option = primaryCategory.options.find(opt => String(opt.id) === String(valueId));
              if (!option) return null;
              return (
                <TagPill key={`primary-${valueId}`} $primary>
                  <TagLabel>{primaryCategory.name}: {option.name}</TagLabel>
                  <RemoveTag onClick={() => handlePrimaryToggle(valueId)}>×</RemoveTag>
                </TagPill>
              );
            })}

            {/* Other Category Tags */}
            {Object.entries(selectedCategories).map(([categoryId, optionId]) => {
              const category = otherCategories.find(cat => String(cat.id) === String(categoryId));
              if (!category) return null;
              
              const option = category.options.find(opt => String(opt.id) === String(optionId));
              if (!option) return null;
              
              return (
                <TagPill key={`category-${categoryId}`}>
                  <TagLabel>{category.name}: {option.name}</TagLabel>
                  <RemoveTag onClick={() => handleCategoryClear(categoryId)}>×</RemoveTag>
                </TagPill>
              );
            })}

            {/* No tags state */}
            {primaryTeamSelections.length === 0 && Object.keys(selectedCategories).length === 0 && (
              <NoTagsText>No tags assigned</NoTagsText>
            )}
          </CurrentTagsContainer>

          {/* Tag Editor */}
          {showTagEditor && (
            <TagEditor>
              <TagEditorRow>
                <StyledDropdown
                  search
                  selection
                  value={editingCategory}
                  onChange={(e, { value }) => {
                    setEditingCategory(value);
                    setEditingValue(''); // Reset value when category changes
                  }}
                  options={getCategoryDropdownOptions()}
                  placeholder="Select a category"
                />

                {editingCategory && editingCategory !== '' && (
                  <StyledDropdown
                    search
                    selection
                    value={editingValue}
                    onChange={(e, { value }) => setEditingValue(value)}
                    options={getValueDropdownOptions()}
                    placeholder="Select value"
                  />
                )}

                <AddButton 
                  onClick={handleAddTag}
                  disabled={!editingCategory || !editingValue}
                >
                  Add
                </AddButton>

                <CancelEditButton onClick={() => {
                  setShowTagEditor(false);
                  setEditingCategory('');
                  setEditingValue('');
                }}>
                  Cancel
                </CancelEditButton>
              </TagEditorRow>
            </TagEditor>
          )}
        </Section>
      </ContentSection>
    </Container>
  );
};

export default EmployeeEditV2;

// Styled Components
const LoadingContainer = styled.div`
  height: 400px;
  position: relative;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
  background: #ffffff;
  min-height: 100vh;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  padding-bottom: 24px;
  border-bottom: 1px solid #e5e5e5;
`;

const Title = styled.h1`
  font-family: "Poppins", sans-serif;
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const PrimaryActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  background: transparent;
  border: 1px solid #ddd;
  border-radius: 8px;
  color: #666;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    background: #f5f5f5;
  }
`;

const SaveButton = styled.button`
  padding: 10px 20px;
  background: #007bff;
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  
  &:not(:disabled):hover {
    background: #0056b3;
  }

  &:disabled {
    background: #cbd5f5;
    color: #ffffff;
    cursor: not-allowed;
  }
`;

const ArchiveButton = styled.button`
  padding: 10px 20px;
  background: #dc2626;
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: #b91c1c;
  }
`;

const ReactivateButton = styled.button`
  padding: 10px 20px;
  background: #10b981;
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: #0f996b;
  }
`;

const UnsavedPill = styled.div`
  padding: 8px 12px;
  background: #FEF3C7;
  border: 1px solid #F59E0B;
  color: #92400E;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 600;
`;

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const Section = styled.div`
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 12px;
  padding: 24px;
`;

const SectionTitle = styled.h2`
  font-family: "Poppins", sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0 0 4px 0;
`;

const SectionSubtitle = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0 0 20px 0;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldGroupFullWidth = styled(FieldGroup)`
  grid-column: 1 / -1;
`;

const FieldLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #333;
`;

const FieldInput = styled.input`
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }
`;

const EmailFieldRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const ReadOnlyField = styled(FieldInput)`
  background: #f9fafb;
  color: #333;
  cursor: text;

  &:read-only {
    cursor: text;
  }
`;

const CopyEmailButton = styled.button`
  padding: 10px 16px;
  background: transparent;
  border: 1px solid #007bff;
  border-radius: 8px;
  color: #007bff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:not(:disabled):hover {
    background: #f0f7ff;
  }

  &:disabled {
    border-color: #d1d5db;
    color: #9ca3af;
    cursor: not-allowed;
  }
`;

const MoreVertIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12,16A2,2 0 0,1 14,18A2,2 0 0,1 12,20A2,2 0 0,1 10,18A2,2 0 0,1 12,16M12,10A2,2 0 0,1 14,12A2,2 0 0,1 12,14A2,2 0 0,1 10,12A2,2 0 0,1 12,10M12,4A2,2 0 0,1 14,6A2,2 0 0,1 12,8A2,2 0 0,1 10,6A2,2 0 0,1 12,4Z" />
  </svg>
);

const MenuTriggerButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  color: #4b5563;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f5f5f5;
    border-color: #c0c0c0;
  }

  &:focus-visible {
    outline: 2px solid #007bff;
    outline-offset: 2px;
  }
`;

const OptionsMenuWrapper = styled.div`
  position: relative;
  z-index: 2;
`;

const FloatingMenu = styled.div`
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  padding: 6px 0;
  min-width: 180px;
  z-index: 1000;
`;

const OptionsMenuItem = styled.button`
  width: 100%;
  padding: 10px 16px;
  background: transparent;
  border: none;
  text-align: left;
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => (props.danger ? "#dc2626" : "#374151")};
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;

  &:not(:disabled):hover {
    background: ${(props) => (props.danger ? "#fff5f5" : "#f3f4f6")};
  }

  &:disabled {
    color: #9ca3af;
    cursor: not-allowed;
  }
`;

const OptionsMenuDivider = styled.div`
  height: 1px;
  background: #e5e7eb;
  margin: 4px 0;
`;

const CategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  position: relative;
`;

const CategoryCard = styled.div`
  padding: 16px;
  border: 2px solid ${props => props.$selected ? '#007bff' : '#e5e5e5'};
  border-radius: 12px;
  cursor: pointer;
  position: relative;
  background: ${props => props.$selected ? '#f8fbff' : '#ffffff'};
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${props => props.$selected ? '#007bff' : '#ccc'};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const CategoryCardTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0 0 4px 0;
`;

const CategoryCardDescription = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0;
  line-height: 1.4;
`;

const SelectedIndicator = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 24px;
  height: 24px;
  background: #007bff;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: bold;
`;

const ClearButton = styled.button`
  padding: 8px 16px;
  background: #f8f9fa;
  border: 1px solid #ddd;
  border-radius: 8px;
  color: #666;
  font-size: 12px;
  cursor: pointer;
  align-self: start;
  
  &:hover {
    background: #e9ecef;
  }
`;

// New Tags Section Styled Components
const TagsSectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const TagsIcon = styled.span`
  font-size: 20px;
`;

const TagsSectionTitle = styled.h2`
  font-family: "Poppins", sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
  flex: 1;
`;

const EditTagsButton = styled.button`
  padding: 8px 16px;
  background: transparent;
  border: 1px solid #007bff;
  border-radius: 6px;
  color: #007bff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    background: #f8fbff;
  }
`;

const CurrentTagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  min-height: 40px;
  align-items: flex-start;
`;

const ModalDescription = styled.p`
  font-size: 14px;
  color: #444;
  line-height: 1.5;
  margin-bottom: 16px;
`;

const ArchiveReasonInput = styled.textarea`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  resize: vertical;
  min-height: 100px;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }
`;

const ModalButton = styled.button`
  padding: 10px 18px;
  border-radius: 8px;
  border: none;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  background: #dc2626;
  color: white;
  margin-left: 12px;

  &:hover {
    background: #b91c1c;
  }

  ${(props) =>
    props.$secondary &&
    `
      background: transparent;
      color: #555;
      border: 1px solid #ddd;
      margin-left: 0;

      &:hover {
        background: #f5f5f5;
      }
    `}
`;

const TagPill = styled.div`
  display: flex;
  align-items: center;
  background: ${props => props.$primary ? '#e3f2fd' : '#f5f5f5'};
  border: 1px solid ${props => props.$primary ? '#2196f3' : '#ddd'};
  border-radius: 20px;
  padding: 6px 4px 6px 12px;
  font-size: 13px;
  color: ${props => props.$primary ? '#1976d2' : '#555'};
  gap: 6px;
`;

const TagLabel = styled.span`
  font-weight: 500;
`;

const RemoveTag = styled.button`
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  padding: 4px;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(0, 0, 0, 0.1);
    color: #333;
  }
`;

const NoTagsText = styled.div`
  color: #999;
  font-style: italic;
  padding: 8px 0;
`;

const TagEditor = styled.div`
  margin-top: 16px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e5e5e5;
`;

const TagEditorRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

const StyledDropdown = styled(Dropdown)`
  min-width: 200px !important;
  
  &.ui.selection.dropdown {
    min-height: auto;
  }
`;

const AddButton = styled.button`
  padding: 8px 16px;
  background: #007bff;
  border: none;
  border-radius: 6px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
  
  &:hover:not(:disabled) {
    background: #0056b3;
  }
`;

const CancelEditButton = styled.button`
  padding: 8px 16px;
  background: transparent;
  border: 1px solid #ddd;
  border-radius: 6px;
  color: #666;
  font-size: 14px;
  cursor: pointer;
  
  &:hover {
    background: #f5f5f5;
  }
`;
